import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Container } from "react-bootstrap";
import {
  FaEuroSign,
  FaEnvelopeOpenText,
  FaBug,
  FaGlobe,
  FaHeartBroken,
  FaEllipsisH,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaTag,
  FaCommentAlt,
  FaCheck,
  FaPaperclip
} from "react-icons/fa";
import { sendContactMessage } from "../../services/contactService";
import { motion, AnimatePresence } from "framer-motion";
import "react-toastify/dist/ReactToastify.css";
import ReCAPTCHA from "react-google-recaptcha";
import "../../css/contactModal.css";

interface ContactModalProps {
  show: boolean;
  onClose: () => void;
}

const reasonsList = [
  { label: "Problème de facturation", icon: <FaEuroSign />, color: "#4BB0C0" },
  { label: "Demande d'assistance", icon: <FaEnvelopeOpenText />, color: "#191a1aff" },
  { label: "Problème technique", icon: <FaBug />, color: "#4BB0C0" },
  { label: "Devenir partenaire", icon: <FaGlobe />, color: "#191a1aff" },
  { label: "Erreur constatée", icon: <FaHeartBroken />, color: "#4BB0C0" },
  { label: "Autre", icon: <FaEllipsisH />, color: "#191a1aff" },
];

const ContactModal: React.FC<ContactModalProps> = ({ show, onClose }) => {
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [reference, setReference] = useState("");
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!show) {
      setSelectedReasons([]);
      setDetails("");
      setLoading(false);
      setSuccessMessage("");
      setName("");
      setContact("");
      setReference("");
      setCaptchaValid(false);
      setCaptchaToken(null);
      setAttachment(null);
    }
  }, [show]);

  const handleCaptchaChange = (value: string | null) => {
    if (value) {
      setCaptchaValid(true);
      setCaptchaToken(value);
    } else {
      setCaptchaValid(false);
      setCaptchaToken(null);
    }
  };

  const validateContact = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const frenchPhoneRegex = /^(0\d{9}|\+33\d{9})$/;
    const europeanPhoneRegex = /^\+[1-9]\d{6,14}$/;

    if (emailRegex.test(value)) {
      return "email";
    } else if (frenchPhoneRegex.test(value.replace(/\s+/g, ""))) {
      return "phone-fr";
    } else if (europeanPhoneRegex.test(value.replace(/\s+/g, ""))) {
      return "phone-eu";
    } else {
      return null;
    }
  };

  const handleCheckboxChange = (reason: string) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
  };

  const handleSubmit = async () => {
    const userEmail = localStorage.getItem("userEmail") || "";

    if (!captchaValid) {
      setSuccessMessage("⚠️ Veuillez valider le captcha avant d'envoyer !");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("reasons", JSON.stringify(selectedReasons));
      formData.append("details", details);
      formData.append("userEmail", userEmail);
      formData.append("name", name);
      formData.append("contact", contact);
      formData.append("reference", reference);
      formData.append("captchaToken", captchaToken || "");

      if (attachment) {
        formData.append("attachment", attachment);
      }

      await sendContactMessage(formData);

      setSuccessMessage("✅ Message envoyé avec succès !");
      setTimeout(() => {
        onClose();
        setSuccessMessage("");
      }, 2000);
    } catch (error) {
      console.error(error);
      setSuccessMessage("❌ Erreur lors de l'envoi du message !");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setAttachment(file);
  };

  // Déterminer la taille du modal selon la largeur de l'écran
  const getModalSize = () => {
    if (windowWidth < 576) return "sm";
    if (windowWidth < 992) return "lg";
    return "xl";
  };

  return (
    <AnimatePresence>
      {show && (
        <Modal 
          show={show} 
          onHide={onClose} 
          size={getModalSize()}
          centered 
          dialogClassName="custom-contact-modal"
          scrollable={windowWidth < 768}
        >
          <motion.div
            initial={{ opacity: 0, y: -30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <Modal.Header closeButton className="modal-header-responsive">
              <Modal.Title className="w-100 text-center">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="modal-title-content"
                >
                  <FaEnvelopeOpenText className="modal-title-icon" />
                  <span>Contacter le support</span>
                </motion.div>
              </Modal.Title>
            </Modal.Header>

            <Modal.Body className="modal-body-responsive">
              <Container fluid className="px-0 px-md-2">
                {/* Section Raisons de contact */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="mb-4"
                >
                  <h6 className="mb-3 fw-semibold text-dark section-title">
                    <FaCommentAlt className="me-2" />
                    Pourquoi souhaitez-vous nous contacter ?
                  </h6>
                  
                  <div className="reasons-grid">
                    {reasonsList.map((reason, index) => (
                      <motion.div
                        key={reason.label}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.03 }}
                      >
                        <div
                          className={`reason-card ${selectedReasons.includes(reason.label) ? 'selected' : ''}`}
                          onClick={() => handleCheckboxChange(reason.label)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleCheckboxChange(reason.label);
                            }
                          }}
                        >
                          <div className="reason-card-content">
                            <div className="reason-icon-container" style={{ color: reason.color }}>
                              {reason.icon}
                            </div>
                            <div className="reason-checkbox-container">
                              <Form.Check
                                type="checkbox"
                                id={`reason-${index}`}
                                label={reason.label}
                                checked={selectedReasons.includes(reason.label)}
                                onChange={() => {}}
                                className="mb-0"
                              />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Section Informations personnelles */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mb-4"
                >
                  <h6 className="mb-3 fw-semibold text-dark section-title">
                    <FaUser className="me-2" />
                    Vos informations
                  </h6>
                  
                  <Row className="g-3">
                    <Col xs={12} md={6}>
                      <Form.Group className="contact-input-group">
                        <Form.Label className="input-label">
                          <FaUser className="me-1" />
                          Nom
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Votre nom complet"
                          className="form-control-sm"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col xs={12} md={6}>
                      <Form.Group className="contact-input-group">
                        <Form.Label className="input-label">
                          {validateContact(contact) === 'email' ? (
                            <FaEnvelope className="me-1" />
                          ) : (
                            <FaPhone className="me-1" />
                          )}
                          Contact
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={contact}
                          onChange={(e) => setContact(e.target.value)}
                          placeholder="Email ou téléphone"
                          className="form-control-sm"
                        />
                        {contact && !validateContact(contact) && (
                          <div className="contact-error-message">
                            <small>
                              ⚠️ Format invalide. Entrez un email ou un numéro français/européen.
                            </small>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    
                    <Col xs={12}>
                      <Form.Group className="contact-input-group">
                        <Form.Label className="input-label">
                          <FaTag className="me-1" />
                          Référence (facultatif)
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={reference}
                          onChange={(e) => setReference(e.target.value)}
                          placeholder="Numéro de commande, référence client..."
                          className="form-control-sm"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </motion.div>

                {/* Section Détails */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mb-4"
                >
                  <Form.Group>
                    <Form.Label className="fw-semibold text-dark section-title">
                      <FaCommentAlt className="me-2" />
                      Détails supplémentaires
                    </Form.Label>
                    
                    {selectedReasons.includes("Autre") && (
                      <div className="alert alert-warning alert-sm mb-3">
                        <FaCheck className="me-2" />
                        Vous avez sélectionné <strong>Autre</strong>, merci de préciser votre demande.
                      </div>
                    )}
                    
                    <Form.Control
                      as="textarea"
                      rows={windowWidth < 768 ? 3 : 4}
                      value={details}
                      onChange={(e) => setDetails(e.target.value)}
                      placeholder="Décrivez votre problème, suggestion ou demande..."
                      className="contact-textarea form-control-sm"
                    />
                  </Form.Group>

                  {/* Pièce jointe */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.35 }}
                    className="mt-3"
                  >
                    <Form.Group>
                      <Form.Label className="input-label">
                        <FaPaperclip className="me-1" />
                        Pièce jointe (image, PDF...)
                      </Form.Label>
                      <div className="file-input-wrapper">
                        <Form.Control
                          type="file"
                          onChange={handleFileChange}
                          className="form-control-sm"
                        />
                        {attachment && (
                          <div className="file-preview mt-2">
                            <small className="text-muted">
                              📎 {attachment.name} ({Math.round(attachment.size / 1024)} KB)
                            </small>
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </motion.div>
                </motion.div>

                {/* Messages d'état */}
                <AnimatePresence>
                  {successMessage && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className={`contact-status-message ${
                        successMessage.includes("✅") ? "success" : "error"
                      }`}
                    >
                      {successMessage.includes("✅") ? (
                        <FaCheck className="fs-5" />
                      ) : (
                        <div className="fs-5">⚠️</div>
                      )}
                      <span className="fw-semibold">{successMessage}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Captcha */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="captcha-container mt-4"
                >
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LcvUcorAAAAAGv2udg6D347J9UvIS2D0fhhG_FI"}
                    onChange={handleCaptchaChange}
                    size={windowWidth < 400 ? "compact" : "normal"}
                  />
                </motion.div>
              </Container>
            </Modal.Body>

            <Modal.Footer className="modal-footer-responsive">
              <Button
                variant="outline-secondary"
                onClick={onClose}
                className="contact-btn contact-btn-cancel"
              >
                Annuler
              </Button>
              
              <motion.div whileTap={{ scale: 0.97 }}>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  className="contact-btn contact-btn-send"
                  disabled={
                    loading ||
                    !captchaValid ||
                    !name.trim() ||
                    !contact.trim() ||
                    !validateContact(contact)
                  }
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <FaEnvelope className="me-2" />
                      Envoyer
                    </>
                  )}
                </Button>
              </motion.div>
            </Modal.Footer>
          </motion.div>
        </Modal>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;